import clsx from "clsx";

/**
 * Internal dependencies
 */
import { NEW_TAB_TARGET, NOFOLLOW_REL } from "../constants";
import { getUpdatedLinkAttributes } from "../utils";

import Settings from "./InspectorControls/Settings";
import useEnter from "./InspectorControls/useEnter";
/**
 * WordPress dependencies
 */
import { __, sprintf } from "@wordpress/i18n";
import {
  useEffect,
  useState,
  useRef,
  useMemo,
  createInterpolateElement,
} from "@wordpress/element";
import { ToolbarButton, Popover } from "@wordpress/components";
import {
  AlignmentControl,
  BlockControls,
  InspectorControls,
  RichText,
  useBlockProps,
  LinkControl,
  __experimentalUseBorderProps as useBorderProps,
  __experimentalUseColorProps as useColorProps,
  __experimentalGetSpacingClassesAndStyles as useSpacingProps,
  __experimentalGetShadowClassesAndStyles as useShadowProps,
  __experimentalGetElementClassName,
  store as blockEditorStore,
  useBlockEditingMode,
  getTypographyClassesAndStyles as useTypographyProps,
  useSettings,
  JustifyContentControl,
} from "@wordpress/block-editor";
import { displayShortcut, isKeyboardEvent } from "@wordpress/keycodes";
import { link, linkOff } from "@wordpress/icons";
import { getBlockBindingsSource } from "@wordpress/blocks";
import { useMergeRefs } from "@wordpress/compose";
import { useSelect } from "@wordpress/data";

const LINK_SETTINGS = [
  ...LinkControl.DEFAULT_LINK_SETTINGS,
  {
    id: "nofollow",
    title: __("Mark as nofollow"),
  },
];

export default function Edit({
  attributes,
  setAttributes,
  className,
  isSelected,
  onReplace,
  mergeBlocks,
  clientId,
  context,
}) {
  const {
    tagName,
    textAlign,
    linkTarget,
    placeholder,
    rel,
    style,
    text,
    url,
    width,
    metadata,
    icon,
    blockAlignment,
    showText,
  } = attributes;

  const TagName = tagName || "a";

  const animation = attributes.animation || "none";

  const [popoverAnchor, setPopoverAnchor] = useState(null);

  function onKeyDown(event) {
    if (isKeyboardEvent.primary(event, "k")) {
      startEditing(event);
    } else if (isKeyboardEvent.primaryShift(event, "k")) {
      unlink();
      richTextRef.current?.focus();
    }
  }

  const borderProps = useBorderProps(attributes);
  const colorProps = useColorProps(attributes);
  const spacingProps = useSpacingProps(attributes);
  const shadowProps = useShadowProps(attributes);
  const ref = useRef();
  const richTextRef = useRef();
  const blockEditingMode = useBlockEditingMode();
  const blockProps = useBlockProps({
    ref: ref,
    onKeyDown,
  });
  const [isEditingURL, setIsEditingURL] = useState(false);
  const isURLSet = !!url;
  const opensInNewTab = linkTarget === NEW_TAB_TARGET;
  const nofollow = !!rel?.includes(NOFOLLOW_REL);
  const isLinkTag = "a" === TagName;

  const {
    createPageEntity,
    userCanCreatePages,
    lockUrlControls = false,
  } = useSelect(
    (select) => {
      if (!isSelected) {
        return {};
      }

      const _settings = select(blockEditorStore).getSettings();

      const blockBindingsSource = getBlockBindingsSource(
        metadata?.bindings?.url?.source
      );

      return {
        createPageEntity: _settings.__experimentalCreatePageEntity,
        userCanCreatePages: _settings.__experimentalUserCanCreatePages,
        lockUrlControls:
          !!metadata?.bindings?.url &&
          !blockBindingsSource?.canUserEditValue?.({
            select,
            context,
            args: metadata?.bindings?.url?.args,
          }),
      };
    },
    [context, isSelected, metadata?.bindings?.url]
  );

  async function handleCreate(pageTitle) {
    const page = await createPageEntity({
      title: pageTitle,
      status: "draft",
    });

    return {
      id: page.id,
      type: page.type,
      title: page.title.rendered,
      url: page.link,
      kind: "post-type",
    };
  }

  function createButtonText(searchTerm) {
    return createInterpolateElement(
      sprintf(
        /* translators: %s: search term. */
        __("Create page: <mark>%s</mark>"),
        searchTerm
      ),
      { mark: <mark /> }
    );
  }

  function startEditing(event) {
    event.preventDefault();
    setIsEditingURL(true);
  }

  function unlink() {
    setAttributes({
      url: undefined,
      linkTarget: undefined,
      rel: undefined,
    });
    setIsEditingURL(false);
  }

  useEffect(() => {
    if (!isSelected) {
      setIsEditingURL(false);
    }
  }, [isSelected]);

  const linkValue = useMemo(
    () => ({ url, opensInNewTab, nofollow }),
    [url, opensInNewTab, nofollow]
  );

  const useEnterRef = useEnter({ content: text, clientId });
  const mergedRef = useMergeRefs([useEnterRef, richTextRef]);

  const removeAnchorTag = (value) => {
    return value.toString().replace(/<\/?a[^>]*>/g, "");
  };

  const [fluidTypographySettings, layout] = useSettings(
    "typography.fluid",
    "layout"
  );

  const typographyProps = useTypographyProps(attributes, {
    typography: {
      fluid: fluidTypographySettings,
    },
    layout: {
      wideSize: layout?.wideSize,
    },
  });

  const hasNonContentControls = blockEditingMode === "default";
  const hasBlockControls =
    hasNonContentControls || (isLinkTag && !lockUrlControls);

  const iconHtml = useMemo(() => {
    if (!icon.enabled || !icon.name || !icon.type) {
      return "";
    }

    try {
      let html = (
        <span
          className={`brandy-button-icon-content`}
          style={{ color: "inherit" }}
        >
          <i
            className={`ph${
              icon.type !== "regular" ? "-" + icon.type : ""
            } ph-${icon.name}`}
          ></i>
        </span>
      );

      // Don't store React element as attribute - just return for rendering
      return html;
    } catch (error) {
      console.error("Error generating icon HTML:", error);
      return "";
    }
  }, [icon.enabled, icon.name, icon.type, icon.position, icon.gap]);

  let isOutlineButton =
    attributes &&
    attributes.className &&
    attributes.className.includes("is-style-outline");
  const wrapperClasses = clsx(
    className,
    "wp-block-button__link",
    colorProps.className,
    borderProps.className,
    typographyProps.className,
    `has-text-align-${textAlign ?? "center"}`,
    {
      // For backwards compatibility add style that isn't
      // provided via block support.
      "no-border-radius": style?.border?.radius === 0,
      [`has-custom-font-size`]: blockProps.style.fontSize,
      [`has-animation-${animation}`]: animation && animation !== "none",
    },
    __experimentalGetElementClassName("button")
  );

  const wrapperStyles = {
    ...(blockProps.style ?? {}),
    // ...blockProps.style,
    ...borderProps.style,
    ...colorProps.style,
    ...spacingProps.style,
    ...shadowProps.style,
    ...typographyProps.style,
    writingMode: undefined,
    gap: icon.enabled && icon.gap && showText !== false ? icon.gap : undefined,
  };

  return (
    <>
      <div
        {...blockProps}
        className={clsx(
          "wp-block-button wp-block-brandy-button alignfull",
          isOutlineButton ? "is-style-outline--2" : "",
          blockProps.className,
          blockAlignment ? `has-block-alignment-${blockAlignment}` : "",
          {
            [`has-custom-width wp-block-brandy-button__width-${width}`]: width,
          }
        )}
        style={{marginInlineStart:0}}
      >
        <div
          className={wrapperClasses}
          style={wrapperStyles}
          ref={setPopoverAnchor}
        >
          {icon.enabled && icon.position === "left" && (
            <span className="brandy-button-icon">{iconHtml}</span>
          )}
          {showText !== false && (
            <RichText
              ref={mergedRef}
              aria-label={__("Button text")}
              className="brandy-button-text"
              placeholder={placeholder || __("Add text...", "brandy-blocks")}
              value={text}
              onChange={(value) => {
                setAttributes({ text: removeAnchorTag(value) });
              }}
              withoutInteractiveFormatting
              onReplace={onReplace}
              onMerge={mergeBlocks}
              identifier="text"
            />
          )}
          {icon.enabled && icon.position === "right" && (
            <span className="brandy-button-icon">{iconHtml}</span>
          )}
        </div>
      </div>
      {hasBlockControls && (
        <BlockControls group="block">
          <JustifyContentControl
            value={blockAlignment}
            onChange={(nextAlign) => {
              setAttributes({ blockAlignment: nextAlign });
            }}
            allowedControls={["left", "center", "right"]}
          />
          {hasNonContentControls && (
            <AlignmentControl
              value={textAlign}
              onChange={(nextAlign) => {
                setAttributes({ textAlign: nextAlign });
              }}
            />
          )}
          {isLinkTag && !lockUrlControls && (
            <ToolbarButton
              name="link"
              icon={!isURLSet ? link : linkOff}
              title={!isURLSet ? __("Link") : __("Unlink")}
              shortcut={
                !isURLSet
                  ? displayShortcut.primary("k")
                  : displayShortcut.primaryShift("k")
              }
              onClick={!isURLSet ? startEditing : unlink}
              isActive={isURLSet}
            />
          )}
        </BlockControls>
      )}
      {isLinkTag &&
        isSelected &&
        (isEditingURL || isURLSet) &&
        !lockUrlControls && (
          <Popover
            placement="bottom-start"
            onClose={() => {
              setIsEditingURL(false);
              richTextRef.current?.focus();
            }}
            anchor={popoverAnchor}
            focusOnMount={isEditingURL ? "firstElement" : false}
            shift
            resize={false}
          >
            <LinkControl
              value={linkValue}
              onChange={({
                url: newURL,
                opensInNewTab: newOpensInNewTab,
                nofollow: newNofollow,
              }) =>
                setAttributes(
                  getUpdatedLinkAttributes({
                    rel,
                    url: newURL,
                    opensInNewTab: newOpensInNewTab,
                    nofollow: newNofollow,
                  })
                )
              }
              onRemove={() => {
                unlink();
                richTextRef.current?.focus();
              }}
              forceIsEditingLink={isEditingURL}
              settings={LINK_SETTINGS}
              createSuggestion={createPageEntity && handleCreate}
              withCreateSuggestion={userCanCreatePages}
              createSuggestionButtonText={createButtonText}
            />
          </Popover>
        )}
      <InspectorControls>
        <Settings
          selectedWidth={width}
          attributes={attributes}
          setAttributes={setAttributes}
        />
      </InspectorControls>
    </>
  );
}
